/**
 * Kjøretøy-markører (buss/bil) som three.js custom layer.
 *
 * En markør = et rundt "hode" (skive) med kjøretøysymbol i modusfarge
 * (buss grønn, bil gul), som SVEVER ~100 m over kjøretøyet uten stolpe.
 * Hodet er et billboard som alltid vender mot kameraet, som stedsnavn-
 * skiltene, og skaleres opp på lav zoom så det er lesbart på vidvinkel.
 *
 * Fase 1: markøren står statisk ved et gitt punkt (rutestart). Senere
 * faser oppdaterer posisjonen langs ruta via setLngLat() for animasjon.
 */

import {
  Camera,
  Scene,
  WebGLRenderer,
  DirectionalLight,
  AmbientLight,
  Matrix4,
  Vector3,
  Quaternion,
  Group,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  CanvasTexture,
  SRGBColorSpace,
  DoubleSide,
} from 'three';
import { MercatorCoordinate } from 'maplibre-gl';
import { groundElevation } from './terrain.js';

// Litt klaring hindrer flimring der veien og DEM-overflaten nesten møtes.
const GROUND_CLEARANCE_M = 0.75;

/** Bakte høyder er ekte meter; queryTerrainElevation er allerede overdrevet. */
export function markerGroundElevation(map, lngLat, profileElevation) {
  const terrain = map.getTerrain();
  const live = terrain ? groundElevation(map, lngLat) : 0;
  const baked = Number.isFinite(profileElevation)
    ? profileElevation * (terrain?.exaggeration ?? 1) : null;
  if (live === null && baked === null) return null;
  return Math.max(live ?? -Infinity, baked ?? -Infinity) + GROUND_CLEARANCE_M;
}

/** Brukerens valg er uavhengig av midlertidig skjuling i tunneler. */
export function wireMarkerControls(markers, root = document) {
  for (const mode of ['bus', 'car']) {
    const box = root.querySelector(`#show-marker-${mode}`);
    if (!box || !markers[mode]) continue;
    const update = () => markers[mode].setVisible(box.checked);
    box.addEventListener('change', update);
    update();
  }
}

/**
 * Tegner et enkelt kjøretøy-symbol (buss eller bil) på en rund canvas,
 * hvit silhuett på modusfarge. Brukes som tekstur på markørhodet.
 */
function vehicleTexture(mode, color) {
  const s = 128;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const ctx = c.getContext('2d');

  // Rund bakgrunn i modusfarge.
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(s / 2, s / 2, s / 2, 0, Math.PI * 2);
  ctx.fill();

  // Hvit kjøretøy-silhuett (side-profil), sentrert.
  ctx.fillStyle = '#ffffff';
  const cx = s / 2;

  if (mode === 'bus') {
    // Buss: høyt, boksete karosseri med vindusrad.
    const bodyW = 78, bodyH = 46, r = 9;
    const x = cx - bodyW / 2, y = (s - bodyH) / 2 - 4;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + bodyW, y, x + bodyW, y + bodyH, r);
    ctx.arcTo(x + bodyW, y + bodyH, x, y + bodyH, r);
    ctx.arcTo(x, y + bodyH, x, y, r);
    ctx.arcTo(x, y, x + bodyW, y, r);
    ctx.closePath();
    ctx.fill();
    // Vindusrad (hull i modusfarge).
    ctx.fillStyle = color;
    const ww = 12, wh = 15, gap = 3, wy = y + 8;
    let wx = x + 8;
    for (let i = 0; i < 4; i++) { ctx.fillRect(wx, wy, ww, wh); wx += ww + gap; }
    // Hjul (hvite).
    ctx.fillStyle = '#ffffff';
    const wheelY = y + bodyH + 3;
    for (const hx of [x + 18, x + bodyW - 18]) {
      ctx.beginPath(); ctx.arc(hx, wheelY, 8, 0, Math.PI * 2); ctx.fill();
    }
  } else {
    // Bil: lav karosseri + hevet kupé (greenhouse) med skrå front/bak.
    const bodyW = 84, bodyH = 20;
    const x = cx - bodyW / 2, y = cx + 2;         // nedre karosseri
    // Nedre karosseri (avrundet).
    const r = 8;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + bodyW, y, x + bodyW, y + bodyH, r);
    ctx.arcTo(x + bodyW, y + bodyH, x, y + bodyH, r);
    ctx.arcTo(x, y + bodyH, x, y, r);
    ctx.arcTo(x, y, x + bodyW, y, r);
    ctx.closePath();
    ctx.fill();
    // Kupé/tak (trapes) oppå midten.
    const cabW = 42, cabH = 20;
    const cxl = cx - cabW / 2, cyt = y - cabH;
    ctx.beginPath();
    ctx.moveTo(cxl + 8, cyt);            // tak venstre
    ctx.lineTo(cxl + cabW - 8, cyt);     // tak høyre
    ctx.lineTo(cxl + cabW + 6, y + 1);   // ned mot bak
    ctx.lineTo(cxl - 6, y + 1);          // ned mot front
    ctx.closePath();
    ctx.fill();
    // Sidevinduer (hull i modusfarge).
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(cxl + 9, cyt + 4);
    ctx.lineTo(cxl + cabW - 9, cyt + 4);
    ctx.lineTo(cxl + cabW, y - 2);
    ctx.lineTo(cxl - 1, y - 2);
    ctx.closePath();
    ctx.fill();
    // Hjul (hvite) under karosseriet.
    ctx.fillStyle = '#ffffff';
    const wheelY = y + bodyH + 1;
    for (const hx of [x + 18, x + bodyW - 18]) {
      ctx.beginPath(); ctx.arc(hx, wheelY, 9, 0, Math.PI * 2); ctx.fill();
    }
  }

  const tex = new CanvasTexture(c);
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

/**
 * @param {object} opts
 * @param {string} opts.id
 * @param {[number,number]} opts.lngLat  startposisjon
 * @param {string} opts.color            hex (modusfarge)
 * @param {number} [opts.headRadius=2.2]   basis plate-radius i meter
 * @param {number} [opts.heightAboveM=100] hvor høyt hodet svever (meter)
 * @param {number} [opts.minzoom]
 */
export function createMarkerLayer({
  id,
  mode,
  lngLat,
  color,
  headRadius = 2.2,
  heightAboveM = 100,
  minzoom,
  hideAboveZoom,
}) {
  let renderer;
  let scene;
  let camera;
  let map;
  let markerGroup;
  let headMesh;

  // Muterbar posisjon (for senere animasjon).
  //
  // Markøren er et custom layer og drapes ikke av terrenget – stolpen må
  // starte på bakkehøyden, ellers stikker den opp av ingenting nede i en
  // dal eller forsvinner inn i en ås. `groundZ` er sist kjente terrenghøyde
  // for `here`; null betyr "DEM-flisen er ikke lastet ennå".
  let here = lngLat;
  let groundZ = null;
  let profileElevation = null;
  let hidden = false;
  let userVisible = true;
  let position = MercatorCoordinate.fromLngLat(lngLat, 0);
  let meterScale = position.meterInMercatorCoordinateUnits();

  function place() {
    position = MercatorCoordinate.fromLngLat(here, groundZ ?? 0);
    meterScale = position.meterInMercatorCoordinateUnits();
  }

  const layer = {
    id,
    type: 'custom',
    renderingMode: '3d',

    /**
     * Flytt markøren (brukes for animasjon i senere faser).
     * @param {[number,number]} next
     * @param {number} [elevation] terrenghøyde i meter. Oppgi den når ruta
     *   har bakt inn høydeprofil (se scripts/bake-route-elevation.py) – da
     *   følger markøren tunnel- og brugradienten i stedet for bakken over.
     */
    setLngLat(next, elevation) {
      here = next;
      profileElevation = Number.isFinite(elevation) ? elevation : null;
      groundZ = null;
      if (map) map.triggerRepaint();
    },

    /**
     * Skjul/vis markøren. Brukes til å la kjøretøyet forsvinne inn i en
     * tunnel: rutene er delt på `structure`, så kalleren kan gjøre
     *
     *   const p = sampler.at(meter);
     *   marker.setLngLat(p.lngLat, p.elevation);
     *   marker.setHidden(p.structure === 'tunnel');
     *
     * Vi skjuler i stedet for å grave markøren ned i terrenget – en stolpe
     * som stikker opp av åsen over tunnelen ville vært verre enn ingenting.
     */
    setHidden(next) {
      if (hidden === next) return;
      hidden = next;
      if (map) map.triggerRepaint();
    },

    setVisible(next) {
      if (userVisible === Boolean(next)) return;
      userVisible = Boolean(next);
      if (map) map.triggerRepaint();
    },

    get visible() { return userVisible && !hidden; },

    onAdd(mapInstance, gl) {
      map = mapInstance;
      camera = new Camera();
      scene = new Scene();

      scene.add(new AmbientLight(0xffffff, 1.1));
      const key = new DirectionalLight(0xffffff, 1.6);
      key.position.set(-0.5, -0.4, 1).normalize();
      scene.add(key);

      // Bygg markør-geometrien i meter (Y = opp i modellrommet).
      // Ingen stolpe: hodet SVEVER `heightAboveM` meter over kjøretøyet,
      // som stedsnavnskiltene. headRadius er BASIS-platestørrelse ved
      // referanse-zoom; selve skjermstørrelsen skaleres i render().
      const group = new Group();

      // Hode – flat plate (billboard) med kjøretøysymbol, vender mot kamera.
      // Vi dropper rotationX for markøren og jobber direkte i mercator-rommet
      // (X=øst, Y=sør, Z=opp), så «opp» er +Z. Hodet svever heightAboveM på Z.
      const tex = vehicleTexture(mode, color);
      const head = new Mesh(
        new PlaneGeometry(headRadius * 2, headRadius * 2),
        new MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false, side: DoubleSide }),
      );
      head.position.set(0, 0, heightAboveM); // svever 100 m rett opp (+Z)
      head.up.set(0, 0, 1);
      group.add(head);

      scene.add(group);
      markerGroup = group;
      headMesh = head;

      renderer = new WebGLRenderer({
        canvas: map.getCanvas(),
        context: gl,
        antialias: true,
      });
      renderer.autoClear = false;
    },

    render(gl, options) {
      if (hidden || !userVisible) return;
      if (minzoom != null && map.getZoom() < minzoom) return;
      // Skjul markøren tett på (når man selv kan se buss/bil): over dette
      // zoom-nivået er markøren overflødig og dekker bare kjøretøyet.
      if (hideAboveZoom != null && map.getZoom() > hideAboveZoom) return;

      // DEM-detaljer kan endres med zoom. Oppdater bakken hver render og
      // bruk den bakte profilen med riktig overdrivelse som fallback/brugulv.
      const h = markerGroundElevation(map, here, profileElevation);
      if (h === null) return; // aldri tegn på havnivå mens vi venter på DEM
      if (h !== groundZ) {
        groundZ = h;
        place();
      }

      const mvp =
        options?.defaultProjectionData?.mainMatrix ??
        options?.modelViewProjectionMatrix ??
        options;
      const m = new Matrix4().fromArray(mvp);

      // Markørens størrelse skal se ~KONSTANT ut i viewporten (ikke vokse i
      // meter når man zoomer inn). Siden piksler-per-meter dobles for hvert
      // zoom-hakk, kompenserer vi med 2^(REF_ZOOM - zoom): full på vidvinkel,
      // og en liten ekstra nedtrapping (shrink) gjør den relativt sett litt
      // mindre tett på. Selve 100 m-svevet over kjøretøyet ligger fast.
      const REF_ZOOM = 11;
      const REF_SIZE_M = 600;             // plate-«høyde» i meter ved REF_ZOOM
      const z = map.getZoom();
      // Litt mindre tett på: 1.0 @ zoom 11 → 0.6 @ zoom 15.
      const shrink = 1 - 0.4 * Math.min(1, Math.max(0, (z - 11) / (15 - 11)));
      const targetSizeM = REF_SIZE_M * Math.pow(2, REF_ZOOM - z) * shrink;
      const baseSizeM = headRadius * 2;

      const s = meterScale;
      const translate = new Matrix4().makeTranslation(position.x, position.y, position.z);
      const scaleM = new Matrix4().makeScale(s, s, s);
      const model = translate.multiply(scaleM);

      // ── EKTE BILLBOARDING ────────────────────────────────────────────────
      // Platen skal være PARALLELL MED SKJERMPLANET (alltid perfekt sirkel).
      // Vi leser hvilke VERDENSRETNINGER som projiseres til skjerm-høyre og
      // skjerm-opp rett ut av MVP-matrisen (radene i øvre 3×3), bygger en
      // ortonormal basis [høyre, opp, normal] av dem, og orienterer hodet
      // etter den. `model` er bare translate + uniform skala (ingen rotasjon),
      // så MVPs rotasjonsdel er den vi trenger – ingen projeksjons-distorsjon.
      //
      // three lagrer column-major: rad r = (e[r], e[r+4], e[r+8]).
      if (headMesh) {
        headMesh.scale.setScalar(targetSizeM / baseSizeM);
        const e = m.elements;
        const right = new Vector3(e[0], e[4], e[8]).normalize();  // → skjerm-X
        const up = new Vector3(e[1], e[5], e[9]).normalize();     // → skjerm-Y
        const normal = new Vector3().crossVectors(right, up).normalize();
        // Re-ortogonaliser opp mot (normal × høyre) for en ren rotasjon.
        up.crossVectors(normal, right).normalize();
        const basis = new Matrix4().makeBasis(right, up, normal);
        headMesh.quaternion.setFromRotationMatrix(basis);
      }

      camera.projectionMatrix = m.multiply(model);
      renderer.resetState();
      renderer.render(scene, camera);
    },
  };

  return layer;
}
